import React, { useCallback, useState, useMemo } from 'react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useAPI from 'services/useAPI';
import createQueryRequest from 'services/createQueryRequest';
import createMutationRequest from 'utils/createMutationRequest';
import useBoolean from 'utils/hooks/useBoolean';

import { useTranslation } from 'localization/useTranslation';
import SimpleTable, { createDefaultActions } from 'components/Common/SimpleTable/SimpleTable';
import _ from 'lodash';
import T from 'localization/LocalizationKeys';
import { useHistory } from 'react-router-dom';
import { groupKeys } from './queries';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import useAlerts from 'utils/hooks/useAlerts';
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal';

import NewGroupDialog from './NewGroupDialog';

const columns = [
  {
    label: T.common.displayname,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: T.common.description,
    path: 'description',
    searchItem: true,
    isSortable: false
  }
];


const AdminGroupsPage = ({ breadcrumbs }) => {
  const api = useAPI();
  const history = useHistory();
  const queryClient = useQueryClient();
  const T = useTranslation();
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const [newGroupDialogOpen, showNewGroupDialog, hideNewGroupDialog] = useBoolean(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);

  const groupsQuery = useQuery(groupKeys.groups(), createQueryRequest(api.core.getGroups));

  const groups = groupsQuery.isError ? [] : groupsQuery.data ?? [];

  const navigateToGroup = useCallback((groupId) => {
    history.push(`/admin/groups/${groupId}`);
  }, [history]);

  const onRowClick = useCallback((row) => {
    navigateToGroup(row.id);
  }, [navigateToGroup]);

  const createGroup = useMutation({
    mutationFn: createMutationRequest(api.core.createGroup),
    onMutate: (group) => {
      // Optimistic create
      const oldData = [...queryClient.getQueryData(groupKeys.groups())];
      queryClient.setQueryData(groupKeys.groups(), old => {
        return [...old, group];
      });
      return { group, oldData };
    },
    onSuccess: (group) => {
      groupsQuery.refetch();
      navigateToGroup(group.id);
    },
    onError: (error, unusedVariables, context) => {
      addErrorAlert(error.message);
      // Readd the old data
      queryClient.setQueryData(groupKeys.groups(), context?.oldData);
    }
  });
  const onDelete = useCallback((group) => setConfirmDeleteItem(group), []);
  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);
  const actions = useMemo(() => createDefaultActions({ onDelete }), [onDelete]);
  const deleteGroup = useMutation({
    mutationFn: createMutationRequest(api.core.deleteGroup),
    onMutate: (deleteItem) => {
      // Optimistic delete
      const oldData = [...queryClient.getQueryData(groupKeys.groups())];
      queryClient.setQueryData(groupKeys.groups(), old => {
        return _.reject(old, { id: deleteItem.id });
      });
      return { deleteItem, oldData };
    },
    onSuccess: () => {
      addSuccessAlert(T.common.request.delete.success);
      setConfirmDeleteItem(null);
      // Refresh list
      groupsQuery.refetch({ throwOnError: true });
    },
    onError: (unusedError, unusedVariables, context) => {
      addErrorAlert(T.common.request.delete.failed);
      // Readd the old data
      queryClient.setQueryData(groupKeys.groups(), context?.oldData);
    }
  });

  const onSave = useCallback((group) => {
    hideNewGroupDialog();
    createGroup.mutate({
      displayName: group.displayName,
      description: group.description,
      organizationId: group.organization.id
    });
  }, [createGroup, hideNewGroupDialog]);

  return (
    <ContentPage breadcrumb={breadcrumbs}>
      { alerts }
      <SimpleTable
        isLoading={groupsQuery.isLoading}
        onRowClick={onRowClick}
        globalActions={[{ onClick: showNewGroupDialog, isLoading: createGroup?.isLoading }]}
        data={groups}
        columns={columns}
        actions={actions}
        compact
        usePaging
      />
      <NewGroupDialog
        isOpen={newGroupDialogOpen}
        onSave={onSave}
        onCancel={hideNewGroupDialog}
      />
      <ConfirmDeleteModal
        isLoading={deleteGroup.isLoading}
        onConfirm={item => deleteGroup.mutate(item.id)}
        item={confirmDeleteItem}
        itemName={confirmDeleteItem?.displayName ?? T.common.noname}
        onCancel={cancelDelete}
      />
    </ContentPage>
  );
};

export default AdminGroupsPage;
